import { Permissions, serializeBitfield, toBitfield, type ChannelDto, type OverwriteTarget } from '@lagerfeuer/shared';
import { useQuery } from '@tanstack/react-query';
import { Check, Link2, Minus, Plus, Search, Trash2, User, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Modal } from '../../components/Modal';
import { SaveBar } from '../../components/SettingsLayout';
import { errorMessage } from '../../lib/api';
import { channelsApi, membersApi } from '../../lib/endpoints';
import { useAppStore } from '../../store/app';
import { displayName, effectiveOverwrites } from '../../store/selectors';
import { toast } from '../../store/ui';
import { roleColor } from '../server-settings/hierarchy';
import { channelPermissionGroups } from '../server-settings/permissionCatalog';
import { applyTriState, grantableInChannel, triState, withOverwrite, type TriState } from './channelHelpers';

interface Target {
  targetType: OverwriteTarget;
  targetId: string;
}

const key = (t: Target) => `${t.targetType}:${t.targetId}`;
const dispatch = useAppStore.getState().dispatch;

/**
 * Kanal-Overrides je Rolle/Mitglied mit Drei-Zustands-Schaltern (Erlauben/Neutral/Verweigern),
 * Synchronisation mit der Kategorie (D16). Nicht selbst besessene Rechte sind gesperrt.
 */
export function OverwritesEditor({ channel }: { channel: ChannelDto }) {
  const serverId = channel.serverId!;
  const roles = useAppStore((s) => s.roles[serverId]);
  const parent = useAppStore((s) => (channel.parentId ? s.channels[channel.parentId] : undefined));
  const users = useAppStore((s) => s.users);
  const members = useAppStore((s) => s.members[serverId]);
  const overwrites = useMemo(() => effectiveOverwrites(useAppStore.getState(), channel), [channel, parent]); // eslint-disable-line react-hooks/exhaustive-deps
  const grantable = useMemo(() => grantableInChannel(useAppStore.getState(), channel), [channel, roles]); // eslint-disable-line react-hooks/exhaustive-deps
  const [extra, setExtra] = useState<Target[]>([]);
  const [selected, setSelected] = useState<Target>({ targetType: 'ROLE', targetId: serverId });
  const [draft, setDraft] = useState<{ allow: bigint; deny: bigint } | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const synced = channel.permissionsSynced && parent?.type === 'CATEGORY';
  const groups = channelPermissionGroups(channel.type);

  const targets = useMemo<Target[]>(() => {
    const list: Target[] = [{ targetType: 'ROLE', targetId: serverId }];
    const seen = new Set(list.map(key));
    for (const t of [...overwrites, ...extra]) {
      if (!seen.has(key(t))) {
        seen.add(key(t));
        list.push({ targetType: t.targetType, targetId: t.targetId });
      }
    }
    const pos = (t: Target) => (t.targetType === 'ROLE' ? (roles?.find((r) => r.id === t.targetId)?.position ?? -1) : -2);
    return list.sort((a, b) => (a.targetId === serverId ? 1 : b.targetId === serverId ? -1 : pos(b) - pos(a)));
  }, [overwrites, extra, roles, serverId]);

  const current = overwrites.find((o) => o.targetType === selected.targetType && o.targetId === selected.targetId);
  const value = draft ?? { allow: toBitfield(current?.allow), deny: toBitfield(current?.deny) };
  const dirty = draft !== null && (draft.allow !== toBitfield(current?.allow) || draft.deny !== toBitfield(current?.deny));

  const label = (t: Target): string => {
    if (t.targetType === 'ROLE') {
      if (t.targetId === serverId) return '@everyone';
      return roles?.find((r) => r.id === t.targetId)?.name ?? 'Gelöschte Rolle';
    }
    const m = members?.[t.targetId];
    const u = m?.user ?? users[t.targetId];
    return u ? displayName(u, m) : `Mitglied ${t.targetId}`;
  };

  const choose = (t: Target) => {
    setSelected(t);
    setDraft(null);
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const body = { allow: serializeBitfield(draft.allow), deny: serializeBitfield(draft.deny) };
      await channelsApi.putOverwrite(channel.id, selected.targetType, selected.targetId, body);
      dispatch('CHANNEL_UPDATE', withOverwrite(useAppStore.getState(), channel, { ...selected, ...body }));
      setDraft(null);
      toast.success('Berechtigungen gespeichert');
    } catch (e) {
      toast.error('Speichern fehlgeschlagen', errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      if (current) {
        await channelsApi.deleteOverwrite(channel.id, selected.targetType, selected.targetId);
        dispatch('CHANNEL_UPDATE', withOverwrite(useAppStore.getState(), channel, { ...selected, remove: true }));
      }
      setExtra((x) => x.filter((t) => key(t) !== key(selected)));
      choose({ targetType: 'ROLE', targetId: serverId });
    } catch (e) {
      toast.error('Entfernen fehlgeschlagen', errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const sync = async () => {
    setBusy(true);
    try {
      await channelsApi.syncPermissions(channel.id);
      dispatch('CHANNEL_UPDATE', { ...channel, permissionsSynced: true, permissionOverwrites: [] });
      setDraft(null);
      setExtra([]);
      toast.success('Mit Kategorie synchronisiert');
    } catch (e) {
      toast.error('Synchronisieren fehlgeschlagen', errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const setBit = (bit: bigint, state: TriState) => setDraft(applyTriState(value, bit, state));

  return (
    <div className="stack" data-testid="overwrites-editor">
      {parent?.type === 'CATEGORY' && channel.type !== 'CATEGORY' && (
        <div className={`notice ${synced ? '' : 'danger'}`} role="note" data-testid="overwrite-sync-status">
          <Link2 size={16} aria-hidden="true" />
          <span style={{ flex: 1 }}>
            {synced ? (
              <>
                Synchronisiert mit der Kategorie <strong>{parent.name}</strong>. Änderungen hier heben die Synchronisation auf.
              </>
            ) : (
              <>
                Nicht mit der Kategorie <strong>{parent.name}</strong> synchronisiert.
              </>
            )}
          </span>
          {!synced && (
            <button className="btn btn-sm" onClick={() => void sync()} disabled={busy} data-testid="overwrite-sync">
              Mit Kategorie synchronisieren
            </button>
          )}
        </div>
      )}
      <div className="overwrites-layout">
        <div className="overwrite-targets">
          <div className="row">
            <span className="label" style={{ flex: 1 }}>
              Rollen/Mitglieder
            </span>
            <button className="icon-btn icon-btn-xs" aria-label="Rolle oder Mitglied hinzufügen" onClick={() => setAdding(true)} data-testid="overwrite-add">
              <Plus size={16} />
            </button>
          </div>
          <ul className="roles-list" data-testid="overwrite-targets">
            {targets.map((t) => {
              const r = t.targetType === 'ROLE' ? roles?.find((x) => x.id === t.targetId) : undefined;
              return (
                <li key={key(t)} className={`role-item ${key(t) === key(selected) ? 'active' : ''}`}>
                  <button className="role-item-main" onClick={() => choose(t)} aria-current={key(t) === key(selected) ? 'true' : undefined} data-testid="overwrite-target" data-target-id={t.targetId}>
                    {t.targetType === 'MEMBER' ? <User size={14} aria-hidden="true" /> : <span className="role-dot" style={{ background: roleColor(r?.color ?? null) ?? undefined }} aria-hidden="true" />}
                    <span className="truncate">{label(t)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="stack overwrite-perms" style={{ gap: '0.25rem' }}>
          <div className="row">
            <h3 className="settings-subheading" style={{ margin: 0, flex: 1 }}>
              Rechte für {label(selected)}
            </h3>
            {selected.targetId !== serverId && (
              <button className="btn btn-ghost btn-sm" onClick={() => void remove()} disabled={busy} data-testid="overwrite-remove">
                <Trash2 size={14} /> Entfernen
              </button>
            )}
          </div>
          {groups.map((g) => (
            <section key={g.id} className="perm-group" aria-label={g.label}>
              <h4 className="label" style={{ margin: '0.6rem 0 0.2rem' }}>
                {g.label}
              </h4>
              {g.items.map((i) => {
                const bit = Permissions[i.name];
                const state = triState({ allow: serializeBitfield(value.allow), deny: serializeBitfield(value.deny) }, bit);
                const locked = (grantable & bit) !== bit;
                return (
                  <div key={i.name} className={`perm-row ${locked ? 'locked' : ''}`} data-testid={`overwrite-row-${i.name}`}>
                    <span className="perm-text">
                      <span className="perm-label">{i.label}</span>
                      <span className="perm-desc">{i.description}</span>
                    </span>
                    <span className="tristate" role="group" aria-label={i.label}>
                      {(
                        [
                          ['deny', 'Verweigern', <X key="d" size={16} />],
                          ['neutral', 'Neutral (erben)', <Minus key="n" size={16} />],
                          ['allow', 'Erlauben', <Check key="a" size={16} />],
                        ] as const
                      ).map(([st, text, icon]) => (
                        <button
                          key={st}
                          type="button"
                          data-state={st}
                          aria-pressed={state === st}
                          aria-label={`${i.label}: ${text}`}
                          title={text}
                          disabled={locked}
                          onClick={() => setBit(bit, st)}
                          data-testid={`overwrite-${i.name}-${st}`}
                        >
                          {icon}
                        </button>
                      ))}
                    </span>
                  </div>
                );
              })}
            </section>
          ))}
        </div>
      </div>
      <SaveBar dirty={dirty} busy={busy} onSave={() => void save()} onReset={() => setDraft(null)} testId="overwrite-save" />
      {adding && (
        <AddTargetDialog
          serverId={serverId}
          existing={new Set(targets.map(key))}
          onClose={() => setAdding(false)}
          onPick={(t) => {
            setExtra((x) => [...x, t]);
            choose(t);
            setAdding(false);
          }}
        />
      )}
    </div>
  );
}

function AddTargetDialog({ serverId, existing, onPick, onClose }: { serverId: string; existing: Set<string>; onPick: (t: Target) => void; onClose: () => void }) {
  const roles = useAppStore((s) => s.roles[serverId]) ?? [];
  const [q, setQ] = useState('');
  const term = q.trim().toLowerCase();
  const roleHits = roles.filter((r) => !r.isEveryone && !existing.has(`ROLE:${r.id}`) && (!term || r.name.toLowerCase().includes(term)));
  const memberQ = useQuery({
    queryKey: ['overwrite-members', serverId, term],
    queryFn: () => membersApi.list(serverId, { q: term, limit: 10 }),
    enabled: term.length > 0,
  });
  const memberHits = (memberQ.data?.items ?? []).filter((m) => !existing.has(`MEMBER:${m.user.id}`));
  return (
    <Modal title="Rolle oder Mitglied hinzufügen" onClose={onClose} testId="overwrite-add-dialog">
      <div className="stack">
        <div className="search-input">
          <Search size={16} aria-hidden="true" />
          <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rollen und Mitglieder suchen" aria-label="Suchen" data-testid="overwrite-add-search" />
        </div>
        {roleHits.length > 0 && (
          <>
            <span className="label">Rollen</span>
            <ul className="roles-list">
              {roleHits.map((r) => (
                <li key={r.id} className="role-item">
                  <button className="role-item-main" onClick={() => onPick({ targetType: 'ROLE', targetId: r.id })} data-testid="overwrite-add-role" data-target-id={r.id}>
                    <span className="role-dot" style={{ background: roleColor(r.color) ?? undefined }} aria-hidden="true" />
                    {r.name}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
        {memberHits.length > 0 && (
          <>
            <span className="label">Mitglieder</span>
            <ul className="roles-list">
              {memberHits.map((m) => (
                <li key={m.user.id} className="role-item">
                  <button
                    className="role-item-main"
                    onClick={() => {
                      useAppStore.getState().update((d) => {
                        d.users[m.user.id] ??= m.user;
                      });
                      onPick({ targetType: 'MEMBER', targetId: m.user.id });
                    }}
                    data-testid="overwrite-add-member"
                    data-target-id={m.user.id}
                  >
                    <User size={14} aria-hidden="true" />
                    {displayName(m.user, m)} <span className="muted small">@{m.user.username}</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
        {roleHits.length === 0 && memberHits.length === 0 && <p className="muted">{term ? 'Keine Treffer.' : 'Tippe, um Mitglieder zu suchen.'}</p>}
      </div>
    </Modal>
  );
}
